import type { QueryClient, QueryKey } from '@tanstack/react-query';
import type {
  FunnelConversation,
  FunnelConversationDocument,
} from 'src/api/generated';
import type {
  ImpactField,
  ImpactQuery,
  QueryImpactFilter,
} from 'src/modules/shared/types/cache/query-impact.types';
import { evaluateQueryImpact } from 'src/modules/shared/utils/cache/query-impact.util';
import { isInfiniteQueryData } from 'src/modules/shared/utils/cache/update.util';
import type { ConversationSearchScope } from '../../../types/conversation-search-refresh.types';
import type { ConversationSearchMatcher } from '../../../types/conversation-search-refresh.types';
import type { ConversationProjection } from '../../../types/cache/funnel-conversations/projection.types';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isFilter = (value: unknown): value is QueryImpactFilter =>
  isRecord(value) &&
  typeof value.field === 'string' &&
  typeof value.operator === 'string' &&
  (value.or === undefined || typeof value.or === 'boolean');

/**
 * Parses a query key without silently discarding malformed predicates.
 * @param key - Full search query identity.
 * @returns Semantics understood by the generic evaluator, or unknown query shape.
 */
export const getConversationImpactQuery = (
  key: QueryKey
): ImpactQuery | undefined => {
  const params: unknown = key[1];
  if (
    !isRecord(params) ||
    (params.search !== undefined && typeof params.search !== 'string') ||
    (params.filters !== undefined &&
      (!Array.isArray(params.filters) || !params.filters.every(isFilter))) ||
    (params.sort !== undefined &&
      (!Array.isArray(params.sort) ||
        !params.sort.every(
          (sort) => isRecord(sort) && typeof sort.field === 'string'
        )))
  ) {
    return undefined;
  }
  return {
    search: typeof params.search === 'string' ? params.search : undefined,
    filters: Array.isArray(params.filters)
      ? params.filters.filter(isFilter)
      : [],
    sort:
      Array.isArray(params.sort) && params.sort.length
        ? params.sort.filter(isRecord).map((sort) => ({
            field: typeof sort.field === 'string' ? sort.field : undefined,
          }))
        : [{ field: 'createdAt' }],
  };
};

/**
 * Converts supplied canonical fields without inventing search enrichment.
 * @param conversation - Accepted canonical snapshot.
 * @returns ID-normalized patch; omitted fields remain unavailable.
 */
export const conversationProjection = (
  conversation: FunnelConversation
): ConversationProjection => ({
  id: String(conversation.id),
  tenantId: conversation.tenantId,
  funnelId: String(conversation.funnelId),
  ...('funnelStatusId' in conversation && {
    funnelStatusId:
      conversation.funnelStatusId == null
        ? null
        : String(conversation.funnelStatusId),
  }),
  ...('userId' in conversation && { userId: conversation.userId }),
  ...('propertyId' in conversation && {
    propertyId:
      conversation.propertyId == null ? null : String(conversation.propertyId),
  }),
  ...('archivedAt' in conversation && { archivedAt: conversation.archivedAt }),
});

/**
 * Restricts local knowledge to canonical scalar and stable sort fields.
 * @param item - Canonical projection or existing enriched row.
 * @param field - Search field requested by the generic evaluator.
 * @returns Explicit field knowledge; unsupported semantics remain unknown.
 */
export const readConversationImpactField = (
  item: object,
  field: string
): ImpactField => {
  if (
    ![
      'id',
      'funnelId',
      'funnelStatusId',
      'userId',
      'propertyId',
      'archivedAt',
      'createdAt',
      'lastInteractionAt',
    ].includes(field)
  ) {
    return { known: false };
  }
  if (!isRecord(item) || !Object.prototype.hasOwnProperty.call(item, field)) {
    return { known: false };
  }
  return { known: true, value: item[field] };
};

const cachedRows = (
  client: QueryClient,
  key: QueryKey
): FunnelConversationDocument[] => {
  const data = client.getQueryData<unknown>(key);
  return isInfiniteQueryData<FunnelConversationDocument>(data)
    ? data.pages.flatMap((page) => page.items)
    : [];
};

/**
 * Retains event-time membership knowledge for queries opened during batching.
 * Unknown history stays conservative instead of reading already-mutated rows.
 * @param conversation - Accepted resource snapshot.
 * @param previous - Canonical state before the update, when known.
 * @param deleted - Whether the resource was removed.
 * @returns Admission rule for new query keys; existing instances use their scope.
 */
export const conversationNewQueryMatcher =
  (
    conversation: FunnelConversation,
    previous?: FunnelConversation,
    deleted = false
  ): ConversationSearchMatcher =>
  (query) => {
    const semantics = getConversationImpactQuery(query.queryKey);
    return (
      !semantics ||
      evaluateQueryImpact({
        query: semantics,
        before: previous,
        after: deleted ? undefined : conversation,
        readField: readConversationImpactField,
        containsItem: false,
      }).reconcile
    );
  };

/**
 * Selects queries whose membership, enrichment or page contents can change.
 * Existing rows supply old membership when canonical history is unavailable.
 * @param client - Owner of cached query instances.
 * @param conversation - Incoming canonical snapshot or deletion snapshot.
 * @param previous - Previous accepted canonical snapshot, if known.
 * @param deleted - Whether the accepted event deletes the resource.
 * @param forceReconcile - Fast-path admission failed; known rows also need search.
 * @returns Exact query instances, including inactive views needing staleness.
 */
export const affectedConversationQueries = (
  client: QueryClient,
  conversation: FunnelConversation,
  previous?: FunnelConversation,
  deleted = false,
  forceReconcile = false
): ConversationSearchScope => {
  const nextPatch = conversationProjection(conversation);
  const previousPatch = previous ? conversationProjection(previous) : undefined;
  const queries = client
    .getQueryCache()
    .findAll({ queryKey: ['crm/documents/funnel-conversations'] });
  const knownRows = queries
    .flatMap((query) => cachedRows(client, query.queryKey))
    .filter(
      (item) =>
        item.id === nextPatch.id && item.tenantId === conversation.tenantId
    );
  // A source row in column A establishes old membership for column C as well.
  // If copies disagree, consider every candidate rather than trusting cache order.
  const candidates = knownRows.length ? knownRows : [undefined];
  return new Set(
    queries.filter((query) => {
      const row = cachedRows(client, query.queryKey).find(
        (item) =>
          item.id === nextPatch.id && item.tenantId === conversation.tenantId
      );
      const semantics = getConversationImpactQuery(query.queryKey);
      if (!semantics) return true;
      return candidates.some((known) => {
        const before = previousPatch
          ? { ...known, ...previousPatch, createdAt: previous?.createdAt }
          : known;
        const after = deleted
          ? undefined
          : { ...known, ...nextPatch, createdAt: conversation.createdAt };
        const comparison = previous ?? known;
        const needsEnrichment =
          forceReconcile ||
          !comparison ||
          String(comparison.contactId ?? '') !==
            String(conversation.contactId) ||
          ['contacts', 'customFields', 'channelSessions'].some((field) => {
            if (
              !isRecord(conversation) ||
              !Object.prototype.hasOwnProperty.call(conversation, field)
            ) {
              return false;
            }
            return (
              !isRecord(comparison) ||
              JSON.stringify(Reflect.get(comparison, field)) !==
                JSON.stringify(Reflect.get(conversation, field))
            );
          }) ||
          (before?.funnelStatusId ?? null) !==
            (nextPatch.funnelStatusId ?? null);
        return evaluateQueryImpact({
          query: semantics,
          before,
          after,
          readField: readConversationImpactField,
          containsItem: !!row,
          needsEnrichment,
        }).reconcile;
      });
    })
  );
};
